import React, { useState, useEffect, useRef } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { ReactComponent as LocationIcon } from '../assets/icons/LocationIcon.svg';
import * as topojson from 'topojson-client';

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const HOVER_DELAY = 50;
const MARKERS_DATA = [
  { id: 'sonoma', coords: [-120.4589, 38.2889], text: 'Wine Business', link: 'https://www.winebusiness.com/' },
  { id: 'alfafar', coords: [-0.3891, 39.4201], text: 'La Semana Vitivinícola', link: 'https://sevi.net/' },
  { id: 'france', coords: [2.3522, 48.8566], text: 'Vitisphere', link: 'https://www.vitisphere.com/' },
  { id: 'montalcino', coords: [11.4892, 43.0592], text: 'WineNews', link: 'https://winenews.it/it/' },
  { id: 'capetown', coords: [20.4232, -30.9249], text: 'winemag.co.za', link: 'https://winemag.co.za/' },
  { id: 'broadview', coords: [138.5997, -34.8778], text: 'Winetitles', link: 'https://winetitles.com.au/' },
  { id: 'auckland', coords: [174.7633, -37.8485], text: 'NZ Wine Growers', link: 'https://www.nzwine.com/en/media/media-releases/?submit=' },
  { id: 'mendoza', coords: [-68.8272, -32.8908], text: 'Uvas Argentinas', link: 'https://uvasargentinas.com.ar/portal-de-noticias/' }
];

const MapMarker = ({ projection, data, handleMouseEnter, handleMouseLeave }) => {
  const [markerX, markerY] = projection(data.coords);
  const isHovered = data.isHovered;
  const circleRadius = 12;
  const circleDefaultFill = '#f8f9fc';
  const circleHoverFill = '#f8f9fc';
  const iconSize = 17;
  const iconDefaultFill = '#454746';
  const iconHoverFill = '#ff1d25';
  const iconX = markerX - iconSize / 2;
  const iconY = markerY - iconSize / 2;

  return (
    <g
      className={`marker-group-${data.id}`}
      onMouseEnter={() => handleMouseEnter(data.id)}
      onMouseLeave={() => handleMouseLeave(data.id)}
      onClick={() => window.open(data.link, '_blank')}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={markerX}
        cy={markerY}
        r={circleRadius}
        filter="url(#dropshadow)"
        fill={isHovered ? circleHoverFill : circleDefaultFill}
        stroke="none"
        strokeWidth={1.5}
      />
      <LocationIcon
        x={iconX}
        y={iconY}
        width={iconSize}
        height={iconSize}
        style={{
          fill: isHovered ? iconHoverFill : iconDefaultFill,
          stroke: 'none',
        }}
      />
    </g>
  );
};

/**
 * Convierte coordenadas SVG (markerX, markerY) a coordenadas relativas
 * al contenedor posicionado (el <div style="position: relative"> padre).
 */
const svgPointToContainerCoords = (svgEl, x, y) => {
  // seguridad
  if (!svgEl || !svgEl.createSVGPoint || !svgEl.getScreenCTM) return { left: x, top: y };

  const pt = svgEl.createSVGPoint();
  pt.x = x;
  pt.y = y;
  const screenPoint = pt.matrixTransform(svgEl.getScreenCTM()); // coordenadas en pantalla (client)
  // busco el contenedor relativo (el closest con position: relative)
  const relativeContainer = svgEl.closest && svgEl.closest('div[style*="position: relative"]');
  const containerRect = relativeContainer ? relativeContainer.getBoundingClientRect() : { left: 0, top: 0 };
  return {
    left: screenPoint.x - containerRect.left,
    top: screenPoint.y - containerRect.top
  };
};

const MapTooltip = ({ svgRef, projection, data, handleMouseEnter, handleMouseLeave }) => {
  // offsets para separar tooltip del círculo (ajustalos aquí)
  const TOOLTIP_OFFSET_X = 15; // mueve horizontalmente (positivo = derecha)
  const TOOLTIP_OFFSET_Y = -13; // mueve verticalmente (negativo = arriba)

  if (!svgRef || !svgRef.current) return null;

  const [markerX, markerY] = projection(data.coords);
  const { left, top } = svgPointToContainerCoords(svgRef.current, markerX, markerY);

  const tooltipContainerStyle = {
    position: 'absolute',
    left: `${left + TOOLTIP_OFFSET_X}px`,
    top: `${top + TOOLTIP_OFFSET_Y}px`,
    zIndex: 100,
    transition: 'opacity 0.18s ease, transform 0.18s ease',
    opacity: data.isHovered ? 1 : 0,
    transform: data.isHovered ? 'translateX(0)' : 'translateX(-6px)',
    pointerEvents: data.isHovered ? 'auto' : 'none' // evita que tooltips invisibles capturen eventos
  };

  const tooltipStyle = {
    background: '#ffffff',
    color: data.isHovered ? '#ff1d25' : '#454746',
    padding: '6px 14px',
    borderRadius: '12px',
    whiteSpace: 'nowrap',
    fontFamily: 'Effra, system-ui, -apple-system, "Segoe UI", Roboto',
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
    display: 'inline-block'
  };

  return (
    <div
      style={tooltipContainerStyle}
      onMouseEnter={() => handleMouseEnter && handleMouseEnter(data.id)}
      onMouseLeave={() => handleMouseLeave && handleMouseLeave(data.id)}
    >
      <a href={data.link} target="_blank" rel="noopener noreferrer" style={tooltipStyle}>
        {data.text}
      </a>
    </div>
  );
};

const Buzziness = () => {
  const [worldData, setWorldData] = useState(null);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const svgRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetch(GEO_URL)
      .then(res => {
        if (!res.ok) throw new Error("Error al cargar datos del mapa.");
        return res.json();
      })
      .then(topoData => {
        const geojsonData = topojson.feature(topoData, topoData.objects.countries);
        setWorldData(geojsonData);
      })
      .catch(error => console.error("Error al cargar o procesar datos:", error));
  }, []);

  useEffect(() => {
    // recalcular la posición de tooltips en resize/scroll para mantener alineación exacta
    const handler = () => {
      // forzamos re-render actualizando estado "virtual" del active marker (sin cambiarlo)
      setActiveMarkerId(id => id);
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, []);

  const handleMouseEnter = (id) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMarkerId(id);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMarkerId(null);
    }, HOVER_DELAY);
  };

  if (!worldData) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Cargando mapa...</div>;
  }

  const width = 910;
  const height = 610;
  const projection = geoMercator().scale(148).translate([410, 430]);
  const pathGenerator = geoPath().projection(projection);

  const markersWithHoverState = MARKERS_DATA.map(marker => ({
    ...marker,
    isHovered: marker.id === activeMarkerId
  }));

  return (
    <div style={{
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative' // <--- contenedor relativo para los tooltips absolutos
    }}>
      <header className="trazabilidad-header" style={{ margin: '0', paddingBottom: '0' }}>
        <div className="header-content" style={{ padding: '0 1em 0px' }}>
          <h1>Buzziness</h1>
        </div>
      </header>

      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ alignSelf: 'center' }}
      >
        <defs>
          <filter id="dropshadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="1" stdDeviation="2" floodColor="#000000" floodOpacity="0.4" />
          </filter>
        </defs>

        <g className="countries">
          {worldData.features.map((d, i) => (
            <path key={`path-${i}`} d={pathGenerator(d)} fill="#e8edf5" stroke="#e8edf5" />
          ))}
        </g>

        <g className="markers">
          {markersWithHoverState.map(data => (
            <MapMarker
              key={`marker-${data.id}`}
              projection={projection}
              data={data}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
            />
          ))}
        </g>
      </svg>

      {/* Tooltips (fuera del SVG, posicionados respecto al contenedor relativo) */}
      {markersWithHoverState.map(data => (
        <MapTooltip
          key={`tooltip-${data.id}`}
          svgRef={svgRef}
          projection={projection}
          data={data}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
        />
      ))}
    </div>
  );
};

export default Buzziness;
